// Client-side filtering/derivation for the full-page chat's history sidebar.
// Kept pure and framework-free so ChatPage can call it without re-fetching,
// and so it's testable without a component-test harness (this repo has none
// - see src/helpers/*.test.js for the pattern these follow).

// Case-insensitive substring match against a conversation's title. Treats a
// missing/blank query as "show everything" rather than "show nothing", and
// tolerates a non-array input so a still-loading/undefined history state
// renders "No chats found" instead of throwing.
export function filterConversations(conversations, query) {
  const list = Array.isArray(conversations) ? conversations : [];
  const trimmed = (query || "").trim().toLowerCase();
  if (!trimmed) return list;
  return list.filter((c) => (c?.title || "").toLowerCase().includes(trimmed));
}

const MAX_TITLE_LENGTH = 48;

// Derives a display title for a conversation from its first user message -
// used before the backend has assigned/returned a persisted title (e.g. an
// optimistic "New chat" entry the moment a user sends their first message,
// ahead of the server round-trip). Falls back to a generic label rather than
// an empty string so the history list never renders a blank row.
export function deriveConversationTitle(messages) {
  const firstUserMessage = (Array.isArray(messages) ? messages : []).find(
    (m) => m?.role === "user" && m?.content?.trim(),
  );
  if (!firstUserMessage) return "New chat";
  const text = firstUserMessage.content.trim().replace(/\s+/g, " ");
  return text.length > MAX_TITLE_LENGTH
    ? `${text.slice(0, MAX_TITLE_LENGTH).trimEnd()}…`
    : text;
}

// Gitcha-API's /v2/help/chat controller hard-rejects any request over 40
// messages (MAX_MESSAGES, helpChat.controller.js) and only ever actually
// *uses* the last 16 anyway (MAX_HISTORY, services/v2/helpChat.js - older
// turns are dropped before the model ever sees them). Since a saved
// conversation's history only ever grows, sending the full thread every
// turn eventually crosses the 40-message wall and the conversation is
// rejected forever with no way to recover except starting a new chat - a
// bug caught in review. Trimming to the server's own effective window here
// keeps every request well under the hard cap AND avoids sending bytes the
// server would just discard.
export const MAX_OUTGOING_MESSAGES = 16;

// `messages` -> the {role, content} pairs to send to the API, trimmed to
// `limit` (keeping the most recent - same tail-keeping behavior as the
// server's own MAX_HISTORY slice, so the outgoing payload's last entry is
// always the current turn) and stripped of any other fields (e.g. React
// key/UI-only state a caller's message objects might carry).
//
// Also drops any `isGreeting` turn - HelpChatPanel.js's client-fabricated
// "Hey - what are you working on?" opener for a brand-new chat. The model
// never produced it, so it must never appear in what the model is shown as
// its own conversation history (a bug caught in review).
export function buildOutgoingMessages(messages, limit = MAX_OUTGOING_MESSAGES) {
  const list = (Array.isArray(messages) ? messages : []).filter(
    (m) => !m?.isGreeting,
  );
  const mapped = list.map(({ role, content }) => ({ role, content }));
  return limit && mapped.length > limit ? mapped.slice(-limit) : mapped;
}

// Buckets conversations into Today / Yesterday / Previous 7 days / Older for
// the history sidebar. A flat list of identically-titled chats ("How does
// matching work?" four times) is unreadable; the date header is what makes
// them distinguishable. Order within a bucket is newest-first, enforced by
// the explicit sort below rather than assumed from the API's response order -
// relying on an undocumented ordering guarantee from the server was a latent
// bug waiting to surface the moment that ordering ever changed (a bug caught
// in review).
//
// `now` is injectable so the buckets are testable without freezing the clock.
export function groupConversationsByDate(conversations, now = new Date()) {
  const list = Array.isArray(conversations) ? conversations : [];
  const startOfDay = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = startOfDay(now);
  const dayMs = 86400000;

  // No timestamp sorts as "newest" (see the loop below, which also buckets
  // it as Today) so an optimistic, not-yet-timestamped row stays at the top
  // instead of being sorted to the bottom by a NaN comparison.
  const sorted = [...list].sort((a, b) => {
    const ta = a?.updatedAt ? new Date(a.updatedAt).getTime() : NaN;
    const tb = b?.updatedAt ? new Date(b.updatedAt).getTime() : NaN;
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
    if (Number.isNaN(ta)) return -1;
    if (Number.isNaN(tb)) return 1;
    return tb - ta;
  });

  const buckets = [
    { key: "today", label: "Today", items: [] },
    { key: "yesterday", label: "Yesterday", items: [] },
    { key: "week", label: "Previous 7 days", items: [] },
    { key: "older", label: "Older", items: [] },
  ];

  for (const c of sorted) {
    // No timestamp (an optimistic row created client-side before the server
    // has echoed one back) is treated as Today rather than dropped.
    const raw = c?.updatedAt;
    const t = raw ? new Date(raw).getTime() : NaN;
    if (!raw || Number.isNaN(t)) {
      buckets[0].items.push(c);
      continue;
    }
    const day = startOfDay(new Date(t));
    if (day >= today) buckets[0].items.push(c);
    else if (day >= today - dayMs) buckets[1].items.push(c);
    else if (day >= today - 7 * dayMs) buckets[2].items.push(c);
    else buckets[3].items.push(c);
  }

  return buckets.filter((b) => b.items.length > 0);
}
